import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function HeroVideoUpload({ videoUrl, thumbnailUrl, onVideoChange, onSave, isSaving }) {
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [error, setError] = useState('');
  const [videoPath, setVideoPath] = useState('');
  const [thumbnailPath, setThumbnailPath] = useState('');
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  const handleVideoSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('video/')) {
      setError('Please select a video file (MP4, WebM, or QuickTime)');
      return;
    }

    // Validate file size (50MB)
    if (file?.size > 50 * 1024 * 1024) {
      setError('Video size must be less than 50MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `hero-video-${Date.now()}.${fileExt}`;
      const filePath = `videos/${fileName}`;

      // Delete old video if exists
      if (videoPath) {
        try {
          await supabase?.storage?.from('hero-videos')?.remove([videoPath]);
        } catch (err) {
          console.error('Error deleting old video:', err);
        }
      }

      const { error: uploadError } = await supabase?.storage?.from('hero-videos')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase?.storage?.from('hero-videos')?.getPublicUrl(filePath);

      setVideoPath(filePath);
      onVideoChange({ url: data?.publicUrl, thumbnailUrl });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please select an image file for thumbnail');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setError('Thumbnail size must be less than 5MB');
      return;
    }

    setUploadingThumbnail(true);
    setError('');

    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `hero-thumbnail-${Date.now()}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;

      // Delete old thumbnail if exists
      if (thumbnailPath) {
        try {
          await supabase?.storage?.from('hero-videos')?.remove([thumbnailPath]);
        } catch (err) {
          console.error('Error deleting old thumbnail:', err);
        }
      }

      const { error: uploadError } = await supabase?.storage?.from('hero-videos')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase?.storage?.from('hero-videos')?.getPublicUrl(filePath);

      setThumbnailPath(filePath);
      onVideoChange({ url: videoUrl, thumbnailUrl: data?.publicUrl });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleRemoveVideo = async () => {
    if (!window.confirm('Are you sure you want to remove this video?')) {
      return;
    }

    try {
      // Delete video file
      if (videoPath) {
        await supabase?.storage?.from('hero-videos')?.remove([videoPath]);
      }

      // Delete thumbnail file
      if (thumbnailPath) {
        await supabase?.storage?.from('hero-videos')?.remove([thumbnailPath]);
      }

      setVideoPath('');
      setThumbnailPath('');
      onVideoChange({ url: '', thumbnailUrl: '' });
    } catch (err) {
      console.error('Error removing video:', err);
      setError('Failed to remove video');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-red-800" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}
      <div className="bg-white bg-opacity-10 rounded-xl p-4 border border-white border-opacity-10">
        <h4 className="font-semibold text-white mb-4">Hero Video</h4>

        <div className="space-y-4">
          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Upload Video (MP4, WebM, or QuickTime)
            </label>
            <input
              type="file"
              ref={videoInputRef}
              onChange={handleVideoSelect}
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => videoInputRef?.current?.click()}
              disabled={uploading}
              iconName={uploading ? 'Loader' : 'Upload'}
              iconPosition="left"
              className="w-full border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
            >
              {uploading ? 'Uploading Video...' : 'Choose Video'}
            </Button>
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Upload Thumbnail (Optional - shown before video loads)
            </label>
            <input
              type="file"
              ref={thumbnailInputRef}
              onChange={handleThumbnailSelect}
              accept="image/*"
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => thumbnailInputRef?.current?.click()}
              disabled={uploadingThumbnail}
              iconName={uploadingThumbnail ? 'Loader' : 'Image'}
              iconPosition="left"
              className="w-full border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
            >
              {uploadingThumbnail ? 'Uploading Thumbnail...' : 'Choose Thumbnail'}
            </Button>
          </div>

          {/* Video Preview */}
          {videoUrl && (
            <div className="mt-4">
              <p className="text-sm font-medium text-white mb-2">Current Video</p>
              <div className="relative">
                <video
                  src={videoUrl}
                  poster={thumbnailUrl || undefined}
                  controls
                  className="w-full h-64 object-cover rounded-xl border border-white border-opacity-20"
                >
                  Your browser does not support the video tag.
                </video>
                <button
                  onClick={handleRemoveVideo}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Remove video"
                >
                  <Icon name="Trash2" size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <Button
          onClick={onSave}
          disabled={isSaving || !videoUrl}
          iconName="Save"
          iconPosition="left"
          className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Video Settings'}
        </Button>
      </div>
      <p className="text-sm text-blue-100 flex items-start">
        <Icon name="Info" size={16} className="mr-2 flex-shrink-0 mt-0.5" />
        The video will play in the hero section video area. Maximum file size is 50MB. Thumbnail is optional and will be shown before the video loads.
      </p>
    </div>
  );
}